import React, { useEffect, useMemo, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Users, X, ClipboardCheck, Home, PlayCircle } from 'lucide-react';
import { type ModuleData, type ModuleId } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useActiveAssignments } from '../../app/hooks/useActiveAssignments';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { PracticeAssignmentBanner } from './PracticeAssignmentBanner';
import { Banner } from '../ui/Banner';
import { PRIMARY } from '../../config/designTokens';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig, PracticeAssignment } from '../../assessment';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { getSmartSessions } from '../../mastery';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { getTimeContext } from '../../utils/timeContext';
import { DailyChallengeCard } from './DailyChallengeCard';
import { GlanceBar } from './GlanceBar';
import { SmartSessionCard } from './SmartSessionCard';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
  onStartHomeworkPractice: (assignment: PracticeAssignment) => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab,
  onStartHomeworkPractice
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant,
    selectedAccessory,
    selectedTier
  } = useDjaySelection();
  // Active assignments — single source of truth via shared hook
  // (audit P1-3). We further filter the assessments by the currently
  // selected group below; practice assignments are not group-scoped.
  const {
    assessments: allAssessments,
    practiceAssignments: activePracticeAssignments
  } = useActiveAssignments(activeAccount?.id);
  const activeAssignments = useMemo(() => allAssessments.filter(a => a.group === selectedGroup), [allAssessments, selectedGroup]);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // R-AUTH-2 (mei 2026): consume `firstFocusSubject` from learner onboarding.
  // The learner picked a subject in onboarding; we now surface a one-time
  // CTA pointing them at it, then clear the pref so it never re-fires.
  const [firstFocusModuleId, setFirstFocusModuleId] = useState<ModuleId | null>(null);
  useEffect(() => {
    if (!activeAccount || activeAccount.role !== 'learner') return;
    let cancelled = false;
    dataLayer.getLearnerPreferences().then(prefs => {
      if (cancelled) return;
      const id = prefs.firstFocusSubject as ModuleId | undefined;
      if (!id) return;
      // Only surface if it's actually a known module for this learner.
      const known = modules.some(m => m.id === id);
      if (known) setFirstFocusModuleId(id);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeAccount, modules]);
  const clearFirstFocus = async () => {
    setFirstFocusModuleId(null);
    try {
      const prefs = await dataLayer.getLearnerPreferences();
      const {
        firstFocusSubject: _omit,
        ...rest
      } = prefs;
      await dataLayer.saveLearnerPreferences(rest as typeof prefs);
    } catch {
      // Non-fatal — the in-memory state is already cleared.
    }
  };
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    let cancelled = false;
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        if (!cancelled) setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (cancelled) return;
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      if (!cancelled) setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  // Time-context (audit LEARNER_HOME_FLOW_UX §P2-5, P2-6) — single source
  // of truth for greeting + Djay sleepy state. Replaces the previous
  // ad-hoc `hour < 8 || hour < 12 → 'Goedemorgen'` block which had a
  // dode tak and lacked a 'Goedenacht' bucket entirely.
  const timeContext = useMemo(() => getTimeContext(), []);
  const greeting = timeContext.greeting;
  // Contextual Djay emotion for the hero (audit §P2-4). Previously this
  // was `getDjayGreeting(displayName).emotion` — a deterministic function
  // of the learner's NAME, so the same child saw the same emotion every
  // single visit regardless of streak, time, or progress. We now reflect
  // the learner's actual state, in priority order.
  const dailyGoalReached = (state.dailyStreak?.todayQuestionsAnswered ?? 0) >= (state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL);
  const heroDjayEmotion: DjayEmotion = useMemo(() => {
    const streakDays = state.dailyStreak?.currentWeekDays ?? 0;
    const startedAny = modules.some(m => getAnsweredQuestions(m.id).length > 0);
    if (dailyGoalReached) return 'proud';
    if (streakDays >= 5) return 'excited';
    if (streakDays === 0 && startedAny) return 'encouraging';
    if (timeContext.djaySleepy) return 'sleeping';
    return 'happy';
  }, [dailyGoalReached, state.dailyStreak?.currentWeekDays, timeContext.djaySleepy, modules, getAnsweredQuestions]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  const level = state.stats.level;
  const xp = state.stats.xp;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  // F4.1 — three-segment Smart Sessions (replaces recommendation + continue + decay-tip)
  const smartSessions = useMemo(() => {
    return getSmartSessions(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer data-id="element-1285">
      {/* Skip-link (audit P2-7) — visually hidden until focused; lets
                      keyboard users jump past the GlanceBar/Hero/banners straight
                      to the primary action (SmartSessionCard / WelcomeCard). */}
      <a href="#main-action" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:font-bold" data-id="element-1286">
        Spring naar startactie
      </a>
      <GlanceBar streakDays={currentStreak} dailyProgress={todayQuestionsAnswered} dailyGoalTarget={dailyGoal} level={level} xp={xp} weeklyGoal={weeklyGoal} weeklyActivity={weeklyActivity} onNavigateToModules={onNavigateToModules} data-id="element-1287" />

      <motion.div variants={itemVariants} className="mb-6 sm:mb-8" data-id="element-1288">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses={PRIMARY.gradient} djayEmotion={heroDjayEmotion} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={[]} data-id="element-1289" />
      </motion.div>

      {/* FIRST-FOCUS BANNER moved (audit P1-4): it now sits directly above
                      the SmartSessionCard / WelcomeCard so the "klaar om te beginnen?"
                      framing is immediately above the Start-CTA. Old position above
                      assignments pushed the primary action below the fold. */}

      {/* KLASCODE BANNER — hidden when assignments are present to reduce clutter */}
      {showKlascodeBanner && activeAssignments.length === 0 && activePracticeAssignments.length === 0 && <motion.div variants={itemVariants} data-id="element-1290">
            <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1291">
              <div className="flex items-center gap-3" data-id="element-1292">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1293">
                  <Users className="w-5 h-5" data-id="element-1294" />
                </div>
                <div data-id="element-1295">
                  <h3 className="text-sm font-bold text-amber-900" data-id="element-1296">
                    Heb je een klascode?
                  </h3>
                  <p className="text-xs text-amber-700 font-medium" data-id="element-1297">
                    Je kunt deze op elk moment invullen via Instellingen.
                  </p>
                </div>
              </div>
              <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1298">
                <X className="w-4 h-4" data-id="element-1299" />
              </button>
            </Card>
          </motion.div>}

      {/* ACTIVE ASSIGNMENTS - Max 1 banner (Assessment > Practice) */}
      {activeAssignments.length > 0 ? <motion.div variants={itemVariants} className="mb-4" data-id="element-1300">
          <Banner variant="assignment" icon={ClipboardCheck} title={`${activeAssignments.length} ${activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'} voor je klaar!`} action={<Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1302">
                Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1303" />
              </Button>} data-id="element-1301">
            Je leraar heeft nieuwe toetsen klaargezet.
          </Banner>
        </motion.div> : activePracticeAssignments.length === 1 ? <motion.div key={activePracticeAssignments[0].id} variants={itemVariants} className="mb-4" data-id="element-1304">
          <PracticeAssignmentBanner assignment={activePracticeAssignments[0]} teacherName="je leraar" onStartPractice={() => {
        const assignment = activePracticeAssignments[0];
        if (assignment.moduleIds.length > 0) {
          onStartHomeworkPractice(assignment);
        } else {
          onNavigateToModules();
        }
      }} data-id="element-1305" />
        </motion.div> : activePracticeAssignments.length > 1 ? <motion.div variants={itemVariants} className="mb-4" data-id="element-1306">
          <Banner variant="assignment" icon={PlayCircle} title={`${activePracticeAssignments.length} huiswerkjes wachten op je!`} action={<Button onClick={() => {
        const first = activePracticeAssignments[0];
        if (first.moduleIds.length > 0) {
          onStartHomeworkPractice(first);
        } else {
          onNavigateToModules();
        }
      }} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1308">
                Start eerste <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1309" />
              </Button>} data-id="element-1307">
            Je leraar heeft meerdere oefen-opdrachten klaargezet.
          </Banner>
        </motion.div> : null}

      {/* QUICK NAVIGATION — mobile only (audit P2-1).
                          On desktop the Sidebar already lists these three routes permanently,
                          so showing them again creates dead duplicate UI above the fold.
                          On mobile the Sidebar lives behind the hamburger, so the trio still
                          earns its place there. */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4 md:hidden" data-id="element-1310">
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} aria-label="Ga naar Mijn Werelden" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1311">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1312">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1313" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1314">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} aria-label="Ga naar Oefenlab" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1315">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1316">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1317" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1318">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} aria-label="Ga naar Toetsen" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1319">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1320">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1321" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1322">
            Toets
          </span>
        </button>
      </motion.div>

      {/* FIRST-FOCUS BANNER (audit P1-4) — eenmalig na onboarding.
                          Staat NÉT boven SmartSessionCard zodat "klaar om met X te beginnen?"
                          direct boven de start-CTA verschijnt i.p.v. boven assignments. */}
      {firstFocusModuleId && (() => {
      const focusModule = modules.find(m => m.id === firstFocusModuleId);
      if (!focusModule) return null;
      return <motion.div variants={itemVariants} data-id="element-1323">
              <Card className="p-4 sm:p-5 border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-white rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm" data-id="element-1324">
                <div className="flex items-center gap-3" data-id="element-1325">
                  <div className="flex-shrink-0" data-id="element-1326">
                    <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} accessory={selectedAccessory} tier={selectedTier} data-id="element-1327" />
                  </div>
                  <div data-id="element-1328">
                    <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-1329">
                      Klaar om met {focusModule.name} te beginnen?
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-1330">
                      Je hebt dit vak gekozen in de welkomstcheck — klik om te
                      starten.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-1331">
                  <Button onClick={() => {
              onStartPractice(firstFocusModuleId);
              void clearFirstFocus();
            }} size="sm" variant="primary" data-id="element-1332">
                    Start <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1333" />
                  </Button>
                  <button type="button" onClick={() => {
              void clearFirstFocus();
            }} aria-label="Verberg deze tip" className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-1334">
                    <X className="w-4 h-4" data-id="element-1335" />
                  </button>
                </div>
              </Card>
            </motion.div>;
    })()}

      {/* PRIMARY ACTION — single SmartSessionCard replaces Continue + Recommendation + Herhaal-tip.
                          Fallback (audit P3-3): if the learner has started somewhere but the
                          smart-session engine returns no segments today, render a soft
                          "kies zelf" CTA instead of letting the surface go silent. */}
      <motion.div id="main-action" variants={itemVariants} className="flex flex-col gap-4 scroll-mt-4" data-id="element-1336">
        {hasStarted ? smartSessions.review || smartSessions.almostExpert || smartSessions.fresh ? <SmartSessionCard sessions={smartSessions} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} onStart={onStartPractice} data-id="element-1337" /> : <Card className="p-5 sm:p-6 border-2 border-slate-100 bg-white rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1338">
              <div className="flex items-center gap-3" data-id="element-1339">
                <div className="flex-shrink-0" data-id="element-1340">
                  <DjayAvatar size="sm" emotion="thinking" variant={selectedVariant} accessory={selectedAccessory} tier={selectedTier} data-id="element-1341" />
                </div>
                <div data-id="element-1342">
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-1343">
                    Geen tip voor vandaag — kies zelf!
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-1344">
                    Loop je werelden door en pak op waar je zin in hebt.
                  </p>
                </div>
              </div>
              <Button onClick={onNavigateToModules} size="sm" variant="primary" className="w-full sm:w-auto" data-id="element-1345">
                Naar werelden <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1346" />
              </Button>
            </Card> : <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} data-id="element-1347" />}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1348">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} onStart={handleStartChallenge} data-id="element-1349" />
        </motion.div>}
    </PageContainer>;
}
import type { DjayAccessory, DjayTier } from '../../mascot';
function WelcomeCard({
  onStart,
  djayVariant,
  djayAccessory,
  djayTier
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
  djayAccessory?: DjayAccessory;
  djayTier?: DjayTier;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1350">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1351" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1352">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1353">
          {/* Audit P3-2: single avatar node, responsive size via the
           sm-breakpoint hidden/block pair. Keeps the visual responsive
           jump but avoids rendering two avatars in the DOM at once. */}
          <span className="sm:hidden" data-id="element-1354">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} accessory={djayAccessory} tier={djayTier} animate data-id="element-1355" />
          </span>
          <span className="hidden sm:inline-block" data-id="element-1356">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} accessory={djayAccessory} tier={djayTier} animate data-id="element-1357" />
          </span>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1358">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1359">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1360">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1361" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}